import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IoMdRefresh } from "react-icons/io";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import { formatCustomDate } from "../../helpers/formatCustomDate";
import { whatsAppService } from "../../services/whatsApp.service";

const getToday = () => new Date().toISOString().split("T")[0];
const getWhatsAppMessages = (data) => data?.whatsapp_message || [];
const getMessageTypes = (data) => data?.message_type || [];

const WhatsAppMessageList = () => {
    const { showToast } = useContext(LogicContext);
    const [messages, setMessages] = useState([]);
    const [messageTypes, setMessageTypes] = useState([]);
    const [fromDate, setFromDate] = useState(getToday);
    const [toDate, setToDate] = useState(getToday);
    const [apiSearchText, setApiSearchText] = useState("");
    const [tableSearchText, setTableSearchText] = useState("");
    const [messageTypeId, setMessageTypeId] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const setResponseData = useCallback((data, keepMessageTypes = false) => {
        setMessages(getWhatsAppMessages(data));
        const types = getMessageTypes(data);
        if (types.length || !keepMessageTypes) setMessageTypes(types);
    }, []);

    const loadMessages = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await whatsAppService.whatsapListList();
            setResponseData(response?.data);
        } catch (error) {
            console.error("Unable to load WhatsApp messages", error);
            showToast?.("Unable to load WhatsApp messages", "error");
            setMessages([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMessages();
    }, []);

    const searchByDate = async () => {
        if (!fromDate || !toDate) {
            showToast?.("Please select both dates", "error");
            return;
        }
        if (fromDate > toDate) {
            showToast?.("From Date cannot be after To Date", "error");
            return;
        }

        setIsLoading(true);
        try {
            const response = await whatsAppService.messageListByDate({ from_date: fromDate, to_date: toDate });
            setResponseData(response?.data, true);
            if (!getWhatsAppMessages(response?.data).length) showToast?.("No messages found for the selected dates", "");
        } catch (error) {
            console.error("Unable to search WhatsApp messages by date", error);
            showToast?.("Unable to search WhatsApp messages by date", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const searchMessages = async (event) => {
        event.preventDefault();
        const searchText = apiSearchText.trim();
        if (!searchText) {
            loadMessages();
            return;
        }

        setIsLoading(true);
        try {
            const response = await whatsAppService.messageSearch(searchText);
            setResponseData(response?.data, true);
            if (!getWhatsAppMessages(response?.data).length) showToast?.("No messages found", "");
        } catch (error) {
            console.error("Unable to search WhatsApp messages", error);
            showToast?.("Unable to search WhatsApp messages", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredMessages = useMemo(() => {
        const searchTerm = tableSearchText.trim().toLowerCase();
        return messages.filter((message) => {
            const matchesType = !messageTypeId || String(message.message_type_id) === messageTypeId;
            const matchesSearch = !searchTerm || Object.values(message).some((value) => String(value ?? "").toLowerCase().includes(searchTerm));
            return matchesType && matchesSearch;
        });
    }, [messageTypeId, messages, tableSearchText]);

    const columns = useMemo(() => [
        { title: "Ref No.", dataIndex: "ref_no", key: "ref_no", sorter: (a, b) => (a.ref_no || 0) - (b.ref_no || 0) },
        { title: "Message Date", dataIndex: "message_date", key: "message_date", sorter: (a, b) => new Date(a.message_date) - new Date(b.message_date), render: (value) => (value ? formatCustomDate(value) : "-") },
        { title: "Message Type", dataIndex: "message_type_name", key: "message_type_name", sorter: (a, b) => (a.message_type_name || "").localeCompare(b.message_type_name || "") },
        { title: "Message Type ID", dataIndex: "message_type_id", key: "message_type_id", sorter: (a, b) => (a.message_type_id || 0) - (b.message_type_id || 0) },
        { title: "Customer Name", dataIndex: "customer_full_name", key: "customer_full_name", sorter: (a, b) => (a.customer_full_name || "").localeCompare(b.customer_full_name || "") },
        { title: "Email Address", dataIndex: "customer_email_address", key: "customer_email_address", sorter: (a, b) => (a.customer_email_address || "").localeCompare(b.customer_email_address || "") },
        { title: "Mobile No.", dataIndex: "customer_mobile_no", key: "customer_mobile_no", sorter: (a, b) => String(a.customer_mobile_no || "").localeCompare(String(b.customer_mobile_no || "")) },
    ], []);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">WhatsApp Messages</h2>
                    <p className="mt-1 text-sm text-gray-500">View and search sent WhatsApp messages.</p>
                </div>
                <button type="button" onClick={loadMessages} disabled={isLoading} className="self-start rounded-full bg-white p-2 shadow-md transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60" title="Refresh messages">
                    <IoMdRefresh className="h-5 w-5 text-gray-600" />
                </button>
            </div>

            <div className="mb-6 rounded-lg bg-white p-4 shadow-md">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                    <div><label htmlFor="from-date" className="mb-1 block text-sm font-medium text-gray-700">From Date</label><input id="from-date" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} className="rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="to-date" className="mb-1 block text-sm font-medium text-gray-700">To Date</label><input id="to-date" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} className="rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <button type="button" onClick={searchByDate} disabled={isLoading} className="rounded-md bg-blue-600 px-4 py-2 text-white shadow-md transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">Search by Date</button>
                    <form onSubmit={searchMessages} className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-end lg:ml-4">
                        <div className="w-full"><label htmlFor="api-search" className="mb-1 block text-sm font-medium text-gray-700">Search Messages</label><input id="api-search" type="search" value={apiSearchText} onChange={(event) => setApiSearchText(event.target.value)} placeholder="Search messages from server..." className="w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                        <button type="submit" disabled={isLoading} className="rounded-md bg-blue-600 px-4 py-2 text-white shadow-md transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">Search</button>
                    </form>
                </div>
            </div>

            <div className="mb-6 flex flex-col gap-4 rounded-lg bg-white p-4 shadow-md md:flex-row md:items-end">
                <div className="w-full md:w-1/3"><label htmlFor="message-type" className="mb-1 block text-sm font-medium text-gray-700">Message Type</label><select id="message-type" value={messageTypeId} onChange={(event) => setMessageTypeId(event.target.value)} className="w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"><option value="">All message types</option>{messageTypes.map((type) => <option key={type.message_type_id} value={String(type.message_type_id)}>{type.message_type_name}</option>)}</select></div>
                <div className="w-full md:w-2/3"><label htmlFor="table-search" className="mb-1 block text-sm font-medium text-gray-700">Search in Table</label><input id="table-search" type="search" value={tableSearchText} onChange={(event) => setTableSearchText(event.target.value)} placeholder="Filter the currently displayed messages..." className="w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            </div>

            {isLoading && <p className="mb-3 text-sm text-gray-500">Loading messages...</p>}
            <GeneralCustomTable tableHeadList={columns} tableDataList={filteredMessages} rowKey="ref_no" />
        </div>
    );
};

export default WhatsAppMessageList;
